import { Injectable } from '@angular/core';

import { GisToolsApi } from '../api/gis-tools-api';
import { ReportsApi } from '../api/reports-api';

@Injectable()
export class ReportHelper {

    constructor(
        private readonly gisToolsService: GisToolsApi,
        private readonly reportsApi: ReportsApi
    ) {

    }

    get mapLayers(): any[] {
        let mapLayers: any[] = [];

        for (const layer of this.mapRef.map.layers.toArray() as any[]) {
            if (layer) {
                if (layer.type === 'group') {
                    mapLayers = this.getLayerGroups(mapLayers, layer);
                } else if (layer.type === 'map-image' && layer.mapcoreProps && !layer.mapcoreProps.hideInLegend) {
                    for (const subLayer of layer.sublayers.items) {
                        mapLayers.push(subLayer);
                    }
                }
            }
        }

        return mapLayers;
    }
    mapRef;
    public reportErrorMessages = {
        noReportData: 'Unable to load report data at this time. Try reloading the report or contact an administrator for help.'
    };
    /**
     * The list of all possible table names for various data types
     */
    public tableNames = {
        mpms: [
            'PROJECT_LOCATION_DETAIL_PT',
            'PROJECT_LOCATION_DETAIL_LN_V',
            'PROJECT_LOCATION_DETAIL_LN'
        ],
        bridges: [
            'BRIDGE_ALL_CENTER_PT',
            'BRIDGE_ALL_LN',
            'BRIDGE_LOCAL_CENTER_PT',
            'BRIDGE_LOCAL_LN'
        ],
        hop: [
            'PROJECT_HWY_OCC_PERMITS_PT',
            'PROJECT_HWY_OCC_PERMITS_LN',
            'PROJECT_HWY_OCC_PERMITS_PUB_LN',
            'PROJECT_HWY_OCC_PERMITS_PUB_PT',
            'PROJECT_HWY_OCCUPANCY_PERMITS',
            'PROJECT_HWY_OCCUP_PERMITS_PUB'
        ]
    };
    async setMapRef() {
        this.mapRef = document.getElementById('mc-map');
    }

    getLayerGroups(mapLayers: any[], layer: any): any[] {
        for (const sub of layer.layers) {
            if (sub) {
                if (sub.type === 'map-image') {
                    for (const subLayer of sub.sublayers.items) {
                        mapLayers.push(subLayer);
                    }
                } else {
                    mapLayers.push(sub);
                }
            }
        }

        return mapLayers;
    }

    // Get county by name from MapCore
    public async getCountyNameFromId(id: number) {
        const result = await this.gisToolsService.getCounties([id]);
        return result[0].name;
    }
    // Get municipal name from MapCore
    public async getMunicipalNameFromId(countyCodes: string[], municipalityCode) {
        const result = await this.gisToolsService.getMunicipalitiesByCounty(countyCodes);
        return result.filter(x => x.municipalityCode === municipalityCode)[0].name;
    }
    // Get congress District name from MapCore
    public async getCongressionalDistrictByName(id: number) {
        const result = await this.gisToolsService.getCongressionalDistricts([id]);
        return result[0];
    }
    // Get senate district name from MapCore
    public async getSenateDistrictByName(id: number) {
        const result = await this.gisToolsService.getSenateDistricts([id]);
        return result[0];
    }
    // Get House district name from MapCore
    public async getHouseDistrictByName(id: number) {
        const result = await this.gisToolsService.getHouseDistricts([id]);
        return result[0];

    }
    public async getLayerIdByName(name: string) {
        return await this.gisToolsService.getLayerIdByName([name]);
    }

    // gets all Asset ID's that the report needs contained in visible layers on the map
    async getIds(columnName: string, tables: string[]) {
        const returnData: number[] = [];
        const mapLayers = this.mapLayers && this.mapLayers.length > 0 ? this.mapLayers : [];
        // let paramConfig = this.mapManagerService.getMap().paramConfig;
        // check to see if a layer is visible on map
        for (const layer of mapLayers) {
            // check to see if the layer table matches the list of tables passed in from report
            if (layer.visible === true && this.compareTableArrays(tables, layer.mapcoreProps.tableNames)) {
                await this.reportsApi.getVisibleAssetIds(layer.mapcoreProps.parentId, columnName, JSON.parse(layer.source.dataSource.query)).then(layers => {
                    for (const dataArr of layers[0].data) {
                        const id = dataArr[0];
                        if (returnData.indexOf(id) === -1) {
                            returnData.push(id);
                        }
                    }
                });
            }
        }
        return returnData;
    }

    compareTableArrays(array1, array2) {
        for (const x of array1) {
            const split1 = x.split('.');
            for (const y of array2) {
                const split2 = y.split('.');
                if (split1[split1.length - 1] === split2[split2.length - 1]) {
                    return true;
                }
            }
        }
        return false;
    }

    getBoundaryLayerProperties(legendService: any) {
        const r = 248;
        const g = 217;
        const b = 28;
        const a = 0.1;
        const polygonSymbol = legendService.getSymbolDefinitionFromGeometry('polygon', [r, g, b, a], [r, g, b]);
        return [polygonSymbol];
    }
}

export type LegislativeType = 'house' | 'senate' | 'congress';
export interface ILegislativeDistrict {
    id: number;
    /**
     * A unique identifier for legislative district that includes the district type and the legislativeDistrictNumber
     * Examples: h-1, s-12
     */
    key: string;
    type: LegislativeType;
    legislativeDistrictNumber: number;
    lastName: string;
    firstName: string;
    homeCounty: string;
    party: string;
    url: string;
    /**
     * In this format: Doe, John (D) - Delaware County
     */
    nameDescription: string;
    /**
     * In this format: 1 (Doe, John (R))
     */
    numberDescription: string;
    /**
     * This is the text that should be displayed in dropdowns
     */
    title: string;
    /**
     * In this format: "[Last], [First] (District [DistrictNumber])""
     * ex: Doe, Johnn 22
     */
    nameNumberDescription: string;
    /**
     * In this format ("[Last], [First] ([House|Congress|Senate] District [DistrictNumber])""
     */
    nameNumberTypeDescription: string;
}
